'use client';

import { useEffect, useState } from 'react';

interface ResultsPanelProps {
  sessionId: string;
}

interface SessionSummary {
  duration: number;
  participantCount: number;
  completionRate: number;
  events: number;
}

export default function ResultsPanel({ sessionId }: ResultsPanelProps) {
  const [summary, setSummary] = useState<SessionSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch telemetry stats for this session
    fetch('/api/telemetry')
      .then((res) => res.json())
      .then((data) => {
        // Demo summary
        setSummary({
          duration: 120,
          participantCount: 1,
          completionRate: 100,
          events: data.bufferedEvents || 0,
        });
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to fetch results:', err);
        setLoading(false);
      });
  }, [sessionId]);

  if (loading) {
    return (
      <div className="border rounded-lg p-6 bg-white dark:bg-gray-900">
        <p className="text-gray-500 text-center">Loading results...</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="border rounded-lg p-6 bg-white dark:bg-gray-900">
        <p className="text-gray-500 text-center">No results available</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-6 bg-white dark:bg-gray-900">
      <h2 className="text-xl font-semibold mb-6">Session Summary</h2>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-500">Duration</p>
          <p className="text-2xl font-bold">{summary.duration}s</p>
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-500">Participants</p>
          <p className="text-2xl font-bold">{summary.participantCount}</p>
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-500">Completion Rate</p>
          <p className="text-2xl font-bold">{summary.completionRate}%</p>
        </div>

        <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4">
          <p className="text-sm text-gray-500">Events Tracked</p>
          <p className="text-2xl font-bold">{summary.events}</p>
        </div>
      </div>

      <div className="text-center text-sm text-gray-500">
        Session ID: {sessionId}
      </div>
    </div>
  );
}
